import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Check, Zap, Shield, Play, Pause, Maximize2, Minimize2, List, Menu } from 'lucide-react';

const LandingPage = () => {
  const [openSection, setOpenSection] = useState(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Hi 👋, I'm your Airbnb Support Assistant! Want to see how I can help guests?" }]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isVideoExpanded, setIsVideoExpanded] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const OPENROUTER_API_KEY = process.env.REACT_APP_OPENROUTER_API_KEY || '';
  const [simulateResponses, setSimulateResponses] = useState(!OPENROUTER_API_KEY);
  const SYSTEM_PROMPT = `You are a helpful Airbnb support assistant. You help guests with common questions about:\n- Check-in/check-out procedures\n- WiFi and amenities\n- House rules\n- Local recommendations\n- Cancellation policies\n\nBe friendly, concise, and professional. If you don't know something specific about the property, politely say so and suggest contacting the host directly.`;
  const videoRef = useRef(null);
  const chatEndRef = useRef(null);

  const toggleSection = (sectionId) => {
  setOpenSection((prev) => (prev === sectionId ? null : sectionId));
  
  // Optional: smooth scroll into view
  setTimeout(() => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, 200);
};

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleVideoHover = (isHovering) => {
    if (videoRef.current && !isVideoExpanded) {
      if (isHovering) {
        videoRef.current.play();
        setIsVideoPlaying(true);
      } else {
        videoRef.current.pause();
        setIsVideoPlaying(false);
      }
    }
  };

  const toggleVideoExpanded = () => {
    setIsVideoExpanded(!isVideoExpanded);
    if (!isVideoExpanded && videoRef.current) {
      videoRef.current.play();
      setIsVideoPlaying(true);
    }
  };

  const handleSendMessage = async () => {
    if (!input.trim()) return;

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    // NOTE: simulated responses disabled — always call OpenRouter below.
    // If you want to re-enable simulation for local testing, uncomment the block below.
    /*
    // If simulateResponses is true or no API key configured, fall back to simulated replies
    if (simulateResponses || !OPENROUTER_API_KEY) {
      setTimeout(() => {
        const responses = [
          "I can help guests with check-in instructions, house rules, and local recommendations instantly!",
          "Need help with booking questions? I provide 24/7 automated support for your Airbnb guests.",
          "I can handle common inquiries like WiFi passwords, checkout procedures, and nearby attractions automatically.",
          "I'm trained on your property details and can respond to guests in seconds, not hours!"
        ];
        const randomResponse = responses[Math.floor(Math.random() * responses.length)];
        setMessages(prev => [...prev, { role: 'assistant', content: randomResponse }]);
        setIsLoading(false);
      }, 800 + Math.floor(Math.random() * 600));
      return;
    }
    */

    // Call OpenRouter API
    try {
      const payload = {
        model: 'meta-llama/llama-4-maverick:free',
        messages: [
          {
            role: 'system',
            content: [ { type: 'text', text: SYSTEM_PROMPT } ]
          },
          {
            role: 'user',
            // Use block format (type:text) to be compatible with the OpenRouter example
            content: [
              { type: 'text', text: input }
            ]
          }
        ]
      };

      const res = await fetch('https://openrouter.ai/api/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${OPENROUTER_API_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`OpenRouter error: ${res.status} ${text}`);
      }

      const json = await res.json();

      // Try to extract assistant text from known response shapes
      let assistantText = '';
      const choice = json?.choices?.[0] || json?.output?.[0] || null;
      if (choice) {
        // OpenRouter often returns choice.message.content as array of blocks
        const message = choice.message || choice;
        const content = message?.content;
        if (typeof content === 'string') assistantText = content;
        else if (Array.isArray(content)) {
          const block = content.find(b => b?.text) || content[0];
          assistantText = block?.text || JSON.stringify(content);
        } else if (content && typeof content === 'object') {
          // maybe { type: 'output_text', text: '...' }
          assistantText = content.text || JSON.stringify(content);
        }
      }

      if (!assistantText) {
        // Fallbacks
        assistantText = json?.choices?.[0]?.message?.content?.[0]?.text || json?.choices?.[0]?.message?.content || json?.result || JSON.stringify(json);
      }

      setMessages(prev => [...prev, { role: 'assistant', content: String(assistantText) }]);
      setIsLoading(false);
    } catch (err) {
      console.error('OpenRouter request failed', err);
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry — something went wrong fetching the assistant response.' }]);
      setIsLoading(false);
    }
  };

  return (
  <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Navigation (centered, rounded header) */}
      <nav className="sticky top-2 z-40 w-full">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
          <div className="mx-auto max-w-4xl">
            <div className="bg-white/70 hover:bg-white/80 backdrop-blur-md hover:backdrop-blur-lg rounded-3xl shadow-lg hover:shadow-xl border border-slate-200/50 hover:border-slate-200 transition-all duration-300 flex items-center justify-between h-16 px-2 md:px-4 py-2">
              <div className="flex items-center space-x-2">
                <MessageCircle className="w-8 h-8 text-cyan-600" />
                <span className="text-xl font-bold text-slate-800">CoHostX</span>
              </div>
              {/* Desktop links (hidden on small screens) */}
              <div className="hidden md:flex items-center justify-end md:flex-1 lg:w-0 space-x-1">
                <a className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 h-9 px-4 py-2 font-medium text-slate-800 hover:text-slate-900 hover:bg-slate-800/10" href="#how-it-works">How It Works</a>
                <a className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 h-9 px-4 py-2 font-medium text-slate-800 hover:text-slate-900 hover:bg-slate-800/10" href="#pricing">Pricing</a>
                <a className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 h-9 px-4 py-2 font-medium text-slate-800 hover:text-slate-900 hover:bg-slate-800/10" href="#legal">Legal</a>
                <div className="h-5 w-px bg-slate-800/20 mx-1" />
                <a className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 h-9 px-4 py-2 font-medium text-white bg-cyan-600 hover:bg-cyan-600/90 rounded-md" href="https://api.whatsapp.com/send?phone=+601139523097&text=Hey">Get Started</a>
              </div>

              {/* Mobile hamburger (visible on small screens) */}
              <div className="flex md:hidden">
                <button
                  type="button"
                  aria-label="Open menu"
                  onClick={() => setIsMobileMenuOpen(true)}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 hover:text-accent-foreground h-8 rounded-md px-3 text-xs text-slate-800 hover:bg-slate-800/10"
                >
                  <Menu className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile menu panel */}
      {isMobileMenuOpen && (
        <div className="px-4 sm:px-6">
          <div className="rounded-2xl backdrop-blur-md bg-white/90 border border-slate-200 shadow-md pointer-events-auto overflow-hidden mt-3">
            <div className="px-4 sm:px-6">
              <div className="flex justify-between items-center" style={{ paddingTop: '1rem', paddingBottom: '1rem' }}>
                <div className="flex items-center">
                  <MessageCircle className="w-8 h-8 text-cyan-600" />
                  <span className="text-xl font-bold text-slate-800 ml-2">CoHostX</span>
                </div>
                <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 rounded-md">
                  <X className="w-6 h-6 text-slate-700" />
                </button>
              </div>
              <div className="flex flex-col space-y-2 pb-4">
                <a className="px-4 py-3 text-slate-800 rounded-md hover:bg-slate-100" href="#how-it-works">How It Works</a>
                <a className="px-4 py-3 text-slate-800 rounded-md hover:bg-slate-100" href="#pricing">Pricing</a>
                <a className="px-4 py-3 text-slate-800 rounded-md hover:bg-slate-100" href="#legal">Legal</a>
                <a className="mt-2 px-4 py-3 bg-cyan-600 text-white rounded-md text-center" href="https://api.whatsapp.com/send?phone=+601139523097&text=Hey">Get Started</a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
  <section className="pt-20 pb-16 px-2 sm:px-4 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
            Automate Your Airbnb
            <span className="block text-cyan-600">Guest Support 24/7</span>
          </h1>
          <p className="text-xl text-slate-600 mb-8 max-w-3xl mx-auto">
            Let AI handle guest inquiries instantly while you focus on growing your hosting business. 
            No more late-night messages or repetitive questions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => setIsChatOpen(true)}
              className="px-8 py-4 bg-cyan-600 text-white rounded-lg text-lg font-semibold hover:bg-cyan-700 transition shadow-lg hover:shadow-xl"
            >
              Try the Demo
            </button>
            <a
              href="#how-it-works"
              className="px-8 py-4 bg-white text-cyan-600 border-2 border-cyan-600 rounded-lg text-lg font-semibold hover:bg-cyan-50 transition"
            >
              See How It Works
            </a>

          </div>
          
          {/* Social Proof */}
          <div className="mt-12 flex flex-wrap justify-center items-center gap-8 text-slate-500">
            <div className="flex items-center space-x-2">
              <Check className="w-5 h-5 text-green-500" />
              <span>A Quick Setup</span>
            </div>
            <div className="flex items-center space-x-2">
              <Check className="w-5 h-5 text-green-500" />
              <span>No Coding Required</span>
            </div>
            <div className="flex items-center space-x-2">
              <Check className="w-5 h-5 text-green-500" />
              <span>24/7 Support</span>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
  <section className="py-16 px-2 sm:px-4 lg:px-8 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-slate-900 mb-12">
            See CoHostX in Action
          </h2>
          <div className="relative">
            <div 
              className={`relative ${isVideoExpanded ? 'fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center p-4' : ''}`}
              onClick={isVideoExpanded ? toggleVideoExpanded : undefined}
            >
              <div 
                className={`relative ${isVideoExpanded ? 'w-full max-w-6xl' : 'w-full'}`}
                onClick={(e) => isVideoExpanded && e.stopPropagation()}
              >
                <video
                  ref={videoRef}
                  className={`w-full ${isVideoExpanded ? 'rounded-none' : 'rounded-2xl'} shadow-2xl cursor-pointer`}
                  onMouseEnter={() => handleVideoHover(true)}
                  onMouseLeave={() => handleVideoHover(false)}
                  onClick={toggleVideoExpanded}
                  loop
                  muted
                  playsInline
                >
                  <source src="/demo.mp4" type="video/mp4" />
                </video>
                
                {/* Video Controls Overlay */}
                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (videoRef.current) {
                        if (isVideoPlaying) {
                          videoRef.current.pause();
                          setIsVideoPlaying(false);
                        } else {
                          videoRef.current.play();
                          setIsVideoPlaying(true);
                        }
                      }
                    }}
                    className="bg-white bg-opacity-90 p-3 rounded-full hover:bg-opacity-100 transition"
                  >
                    {isVideoPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                  </button>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleVideoExpanded();
                    }}
                    className="bg-white bg-opacity-90 p-3 rounded-full hover:bg-opacity-100 transition"
                  >
                    {isVideoExpanded ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
  <section id="how-it-works" className="py-20 px-2 sm:px-4 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center text-slate-900 mb-16">
            How It Works
          </h2>
          <div className="space-y-8">
            {/* First row with three items */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition">
                <div className="w-14 h-14 bg-cyan-100 rounded-xl flex items-center justify-center mb-6">
                  <List className="w-7 h-7 text-cyan-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-4">1. Cohost X Requirements</h3>
                <p className="text-slate-600">
                  We will be taking your Airbnb account details, property information, house rules, and common guest questions to set up your AI assistant.
                </p>
              </div>
              
              <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition">
                <div className="w-14 h-14 bg-cyan-100 rounded-xl flex items-center justify-center mb-6">
                  <Zap className="w-7 h-7 text-cyan-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-4">2. Seamless Integration</h3>
                <p className="text-slate-600">
                  Connect your Airbnb account in minutes. Our AI learns your property details, house rules, and common guest questions automatically.
                </p>
              </div>
              
              <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition">
                <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-6">
                  <MessageCircle className="w-7 h-7 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-4">3. 24/7 Auto-Response</h3>
                <p className="text-slate-600">
                  Guests get instant answers to check-in instructions, WiFi passwords, local tips, and more. No waiting, no frustration.
                </p>
              </div>                            
            </div>

            {/* Second row with two items */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition">
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                  <Shield className="w-7 h-7 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-4">3. Smart Escalation</h3>
                <p className="text-slate-600">
                  Complex issues? The bot notifies you immediately. Simple questions? Handled automatically. You stay in control.
                </p>
              </div>

              <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition">
                <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-6">
                  <Check className="w-7 h-7 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-4">4. Streamlined Cohosting</h3>
                <p className="text-slate-600">
                  Simplify your cohosting tasks with automated communication, booking management, and instant updates for your cohosts and guests.
                </p>
              </div>

              <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition">
                <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mb-6">
                  <Zap className="w-7 h-7 text-orange-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-4">5. Analytics & Insights</h3>
                <p className="text-slate-600">
                  Track guest satisfaction, response times, and common queries. Use data-driven insights to improve your hosting experience.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
  <section id="pricing" className="py-20 px-2 sm:px-4 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center text-slate-900 mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-center text-slate-600 mb-16 text-lg">
            Choose the plan that fits your hosting business
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Starter Plan */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-slate-200 hover:border-cyan-600 transition">
              <h3 className="text-2xl font-bold text-slate-900 mb-2">Starter</h3>
              <div className="mb-6">
                <span className="text-4xl font-bold text-slate-900">$1,001</span>
                <span className="text-slate-600">/setup</span>
              </div>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-600">Up to 3 properties</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-600">1,000 messages/month</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-600">Basic customization</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-600">Email support</span>
                </li>
              </ul>
              <div className="mb-2 p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">Integration & VPS Fees: Included</p>
                <p className="text-sm text-slate-600"></p>
                {/* Add costs */}
              </div>
              <button className="w-full py-3 bg-slate-100 text-slate-900 rounded-lg font-semibold hover:bg-slate-200 transition">
                <a
                  href="https://api.whatsapp.com/send?phone=+601139523097&text=Hey"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full h-full text-slate-900"
                >
                  Get Started
                </a>
              </button>
            </div>

            {/* Pro Plan */}
            <div className="bg-white rounded-2xl shadow-2xl p-8 border-2 border-cyan-600 relative transform scale-105">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-cyan-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                  Most Popular
                </span>
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-2">Professional</h3>
              <div className="mb-6">
                <span className="text-4xl font-bold text-slate-900">$2,000</span>
                <span className="text-slate-600">/setup</span>
              </div>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-600">Up to 10 properties</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-600">5,000 messages/month</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-600">Advanced customization</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-600">Priority support</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-600">Analytics dashboard</span>
                </li>
              </ul>
              <div className="mb-2 p-4 bg-cyan-50 rounded-lg">
                 <p className="text-sm text-slate-600">Integration & VPS Fees: Included</p>
                <p className="text-sm text-slate-600"></p>
              </div>
              <button className="w-full py-3 bg-cyan-600 text-white rounded-lg font-semibold hover:bg-cyan-700 transition shadow-lg">
                <a
                  href="https://api.whatsapp.com/send?phone=+601139523097&text=Hey"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full h-full text-white"
                >
                  Get Started
                </a>
              </button>
            </div>

            {/* Enterprise Plan */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-slate-200 hover:border-cyan-600 transition">
              <h3 className="text-2xl font-bold text-slate-900 mb-2">Enterprise</h3>
              <div className="mb-6">
                <span className="text-4xl font-bold text-slate-900">Custom</span>
              </div>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-600">Unlimited properties</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-600">Unlimited messages</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-600">White-label solution</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-600">Dedicated account manager</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-600">Custom integrations</span>
                </li>
              </ul>
              <div className="mb-2 p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">Integration & VPS Fees: Included</p>
                <p className="text-sm text-slate-600"></p>
              </div>
              <button className="w-full py-3 bg-slate-100 text-slate-900 rounded-lg font-semibold hover:bg-slate-200 transition">
                <a
                  href="https://api.whatsapp.com/send?phone=+601139523097&text=Hey"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-900"
                >
                  Contact Sales
                </a>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial */}
  <section className="py-20 px-2 sm:px-4 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-white p-10 rounded-2xl shadow-xl">
            <p className="text-2xl text-slate-700 mb-6 italic">
              "CoHostX saved me 10+ hours per week. My guests get instant answers, and my reviews have never been better. It's like having a 24/7 assistant!"
            </p>
            <div className="flex items-center justify-center space-x-4">
              <div className="w-12 h-12 bg-cyan-600 rounded-full flex items-center justify-center text-white font-bold">
                SM
              </div>
              <div className="text-left">
                <p className="font-semibold text-slate-900">Simon Fernandez</p>
                <p className="text-slate-600 text-sm">Superhost, 12 Properties</p>
              </div>
            </div>
          </div>
        </div>
      </section>

{/* Terms and Privacy Section */}
<section id="legal" className="py-20 px-2 sm:px-4 lg:px-8 bg-white border-t border-slate-200">
  <div className="max-w-4xl mx-auto">
    <h2 className="text-3xl font-bold text-center text-slate-900 mb-12">
      Legal Information
    </h2>

    {/* Terms and Conditions */}
    <ExpandableSection
  id="terms"
  title="Terms and Conditions"
  content={`Welcome to CoHostX! By using our platform, you agree to our terms of service. 
  This includes compliance with Airbnb policies and local regulations. 
  You are responsible for the information you provide, and you agree not to use our AI assistant 
  for unlawful or unauthorized purposes. CoHostX reserves the right to modify, suspend, 
  or discontinue services at any time with or without notice. Please read these terms carefully 
  before using our services.`}
  isOpen={openSection === "terms"}
  onToggle={() => toggleSection("terms")}
/>

<ExpandableSection
  id="privacy"
  title="Privacy Policy"
  content={`At CoHostX, we are committed to protecting your personal data in accordance with the General Data Protection Regulation (EU) 2016/679 (GDPR) and applicable data protection laws in France and Malaysia.

Data Controller
CoHostX, based in Paris, France, and Shah Alam, Malaysia, is the controller responsible for processing your personal data.
Contact: info@qwhosting.com

Data We Collect
We collect data necessary to operate our Airbnb automation service, including:

Account and contact details (name, email, phone)

Property and booking data

Communication logs with guests (for AI training and support)

Purpose of Processing
We process your data to:

Deliver and maintain CoHostX services

Respond to support requests

Improve AI performance

Comply with legal obligations

Lawful Basis
Processing is based on:

Performance of a contract (service delivery)

Legitimate interest (service improvement and analytics)

Legal obligations (tax or regulatory compliance)

Consent (where applicable, e.g., marketing communications)

Data Retention
We retain personal data only as long as necessary to provide our services or comply with legal requirements.
You may request deletion or anonymization of your data at any time.

Data Sharing
We do not sell or share your data with third parties for marketing.
Data may be shared only with trusted service providers (e.g., hosting or analytics) under strict confidentiality agreements.

Your Rights
Under GDPR and relevant Malaysian data protection laws, you have the right to:

Access, rectify, or delete your data

Restrict or object to processing

Request data portability

File a complaint with CNIL (France’s data protection authority) or the Malaysian Personal Data Protection Department (JPDP)

Contact
For any privacy-related inquiries or data requests, contact:
📧 info@qwhosting.com

📍 Paris, France
📍 Shah Alam, Malaysia

CoHostX ensures all data is handled securely and transparently in accordance with European and Malaysian privacy standards.`}
  isOpen={openSection === "privacy"}
  onToggle={() => toggleSection("privacy")}
/>
  </div>
</section>


      {/* Footer */}
    <footer className="bg-slate-900 border-t border-slate-800 rounded-b-3xl mb-8 mt-20 mx-2 sm:mx-4 lg:mx-[14rem]">
  <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-8"> {/* Adjusted py-12 to py-8 */}
  <div className="flex flex-col lg:flex-row justify-between items-center space-y-8 lg:space-y-0">
      {/* Logo and Description - centered on mobile */}
      <div className="w-full lg:w-auto text-center lg:text-left flex-shrink-0">
        <div className="flex items-center justify-center lg:justify-start space-x-3">
          <div className="h-10 w-10 bg-cyan-600 rounded-xl flex items-center justify-center">
            <MessageCircle className="w-6 h-6 text-white" />
          </div>
          <span className="text-xl font-bold text-white">CoHostX</span>
        </div>
        <p className="text-sm text-slate-400 mt-4 max-w-sm mx-auto lg:mx-0">
          Automate your Airbnb guest support with AI-powered assistance.
        </p>
      </div>

      {/* Footer grid: on mobile show Product & Legal side-by-side, Contact below */}
      <div className="w-full mt-6 lg:mt-0">
  <div className="grid grid-cols-2 md:grid-cols-3 gap-8 text-sm w-full">
          <div>
            <h4 className="font-semibold text-white mb-4">Product</h4>
            <ul className="space-y-2">
              <li><a href="#how-it-works" className="text-slate-400 hover:text-cyan-500 transition-colors">How It Works</a></li>
              <li><a href="#pricing" className="text-slate-400 hover:text-cyan-500 transition-colors">Pricing</a></li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-white mb-4">Legal</h4>
            <ul className="space-y-2">
              <li>
                <button
                  onClick={() => toggleSection("terms")}
                  className="text-slate-400 hover:text-cyan-500 transition-colors"
                >
                  Terms
                </button>
              </li>
              <li>
                <button
                  onClick={() => toggleSection("privacy")}
                  className="text-slate-400 hover:text-cyan-500 transition-colors"
                >
                  Privacy
                </button>
              </li>
            </ul>
          </div>

          {/* Contact row spans full width on mobile */}
          <div className="col-span-2 md:col-span-1">
            <h4 className="font-semibold text-white mb-4">Contact</h4>
            <ul className="space-y-2">
              <li>
                <a href="mailto:info@qwhosting.com" className="text-slate-400 hover:text-cyan-500 transition-colors">info@qwhosting.com</a>
              </li>
              <li>
                <a href="mailto:contact@qwhosting.fr" className="text-slate-400 hover:text-cyan-500 transition-colors">contact@qwhosting.fr</a>
              </li>
              <li>
                <a href="https://api.whatsapp.com/send?phone=+601139523097&text=Hey" className="text-slate-400 hover:text-cyan-500 transition-colors">+60 11-3952 3097</a>
              </li>
              <li className="text-slate-400">Paris, France</li>
              <li className="text-slate-400">Shah Alam, Malaysia</li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <div className="border-t border-slate-800/50 mt-8 pt-8 text-center text-sm text-slate-400">
      <p>&copy; 2025 CoHostX. All rights reserved.</p>
    </div>
  </div>
</footer>

      {/* Floating Chat Widget */}
      {/* Floating Chat Widget & WhatsApp Widget */}
      {!isChatOpen && (
        <>
          <button
            onClick={() => setIsChatOpen(true)}
            className="fixed bottom-6 right-6 w-16 h-16 bg-cyan-600 text-white rounded-full shadow-2xl hover:bg-cyan-700 transition flex items-center justify-center z-50 hover:scale-110"
          >
            <MessageCircle className="w-7 h-7" />
          </button>
          <a
            href="https://api.whatsapp.com/send?phone=+601139523097&text=Hey"
            target="_blank"
            rel="noopener noreferrer"
            className="fixed bottom-6 left-6 w-16 h-16 bg-green-500 text-white rounded-full shadow-2xl hover:bg-green-600 transition flex items-center justify-center z-50 hover:scale-110"
            aria-label="Contact on WhatsApp"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" fill="currentColor" className="w-8 h-8">
              <path d="M16 3C9.373 3 4 8.373 4 15c0 2.385.668 4.66 1.934 6.66L4 29l7.523-1.91A12.96 12.96 0 0016 27c6.627 0 12-5.373 12-12S22.627 3 16 3zm0 22c-1.97 0-3.89-.57-5.523-1.646l-.393-.244-4.47 1.136 1.19-4.36-.256-.41A9.96 9.96 0 016 15c0-5.514 4.486-10 10-10s10 4.486 10 10-4.486 10-10 10zm5.07-7.43c-.276-.138-1.63-.805-1.882-.897-.252-.092-.435-.138-.618.138-.184.276-.71.897-.87 1.083-.161.184-.322.207-.598.069-.276-.138-1.165-.429-2.22-1.366-.82-.73-1.374-1.63-1.535-1.906-.161-.276-.017-.425.121-.563.124-.123.276-.322.414-.483.138-.161.184-.276.276-.46.092-.184.046-.345-.023-.483-.069-.138-.618-1.492-.847-2.043-.223-.537-.45-.464-.618-.472l-.53-.009c-.184 0-.483.069-.737.345-.253.276-.966.945-.966 2.304s.989 2.67 1.127 2.857c.138.184 1.947 2.98 4.72 4.057.661.285 1.176.456 1.578.583.663.211 1.267.181 1.744.11.532-.079 1.63-.666 1.862-1.308.23-.643.23-1.193.161-1.308-.069-.115-.253-.184-.529-.322z"/>
            </svg>
          </a>
        </>
      )}

      {/* Chat Window */}
      {isChatOpen && (
        <div className="fixed bottom-6 right-6 w-96 h-[600px] bg-white rounded-2xl shadow-2xl flex flex-col z-50 border border-slate-200">
          {/* Chat Header */}
          <div className="bg-gradient-to-r from-cyan-600 to-cyan-700 text-white p-4 rounded-t-2xl flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <MessageCircle className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold">CoHostX Demo</h3>
                <p className="text-xs text-cyan-100">Always online</p>
              </div>
            </div>
            <button
              onClick={() => setIsChatOpen(false)}
              className="hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Chat Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] p-3 rounded-2xl ${
                    msg.role === 'user'
                      ? 'bg-cyan-600 text-white rounded-br-none'
                      : 'bg-slate-100 text-slate-800 rounded-bl-none'
                  }`}
                >
                  {msg.content}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-slate-100 p-3 rounded-2xl rounded-bl-none">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Chat Input */}
          <div className="p-4 border-t border-slate-200">
            <div className="flex space-x-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                placeholder="Ask me anything..."
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-600"
              />
              <button
                onClick={handleSendMessage}
                disabled={!input.trim()}
                className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
            {/* Simulation UI removed — app always uses OpenRouter. */}
          </div>
        </div>
      )}
    </div>
  );
};

const ExpandableSection = ({ id, title, content, isOpen, onToggle }) => {
  return (
    <div id={id} className="mb-6 border border-slate-200 rounded-2xl shadow-sm">
      <button
        onClick={onToggle}
        className="w-full flex justify-between items-center p-6 text-left text-lg font-semibold text-slate-800 hover:bg-slate-50 rounded-2xl transition"
      >
        {title}
        <svg
          className={`w-5 h-5 text-slate-500 transform transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      <div
        className={`px-6 pb-4 text-slate-600 text-sm leading-relaxed whitespace-pre-line transition-all duration-500 ease-in-out ${
          isOpen ? 'max-h-[1200px] opacity-100 overflow-y-auto' : 'max-h-0 opacity-0 overflow-hidden'
        }`}
      >
        {content}
      </div>
    </div>
  );
};


export default LandingPage;